/**
 * ModerationWorkspace.jsx — Phase 2C C3: Moderation Resolution Workspace
 *
 * Acceptance rules enforced:
 *   1. Moderator sees both primary and second rounds (no anchoring bias here)
 *   2. diff_pct shown with threshold comparison
 *   3. Evidence panel (left) + resolution panel (right)
 *   4. Criterion-level side-by-side comparison
 *   5. Three frozen resolution choices: USE_PRIMARY / USE_SECOND / USE_MODERATOR_VALUE
 *   6. USE_MODERATOR_VALUE requires moderator_mark within max_mark
 *   7. After resolution: locked view — no further edits possible
 *   8. Canonical resolved_mark written only via service (not frontend arithmetic)
 *   9. Student 403 via EvaluationShell
 *  10. Relationship check: actor must be assigned as MODERATOR (server enforced)
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Lock,
  CheckCircle2,
  AlertTriangle,
  Scale,
  Eye,
  FileText,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Send,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw Object.assign(
      new Error(body?.detail?.message || (typeof body?.detail === "string" ? body.detail : null) || r.statusText),
      { status: r.status, detail: body?.detail }
    );
  }
  return body;
}

const RESOLUTION_OPTIONS = [
  {
    value: "USE_PRIMARY",
    label: "Accept Primary Mark",
    desc: "Resolve using the primary marker's awarded mark.",
    color: "sky",
  },
  {
    value: "USE_SECOND",
    label: "Accept Second Mark",
    desc: "Resolve using the second marker's awarded mark.",
    color: "teal",
  },
  {
    value: "USE_MODERATOR_VALUE",
    label: "Set Moderator Mark",
    desc: "Override with a moderator-determined mark. Requires justification.",
    color: "amber",
  },
];

// ─── Diff badge ──────────────────────────────────────────────────────────────

function DiffBadge({ diffPct, threshold }) {
  const exceeded = threshold != null && diffPct > threshold;
  return (
    <span
      data-testid="diff-pct-badge"
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${
        exceeded
          ? "bg-orange-950/40 text-orange-300 border-orange-800/60"
          : "bg-teal-950/40 text-teal-300 border-teal-800/60"
      }`}
    >
      {exceeded ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
      Diff: {Number(diffPct).toFixed(1)}%
      {threshold != null && (
        <span className="text-slate-500 font-normal">(threshold: {threshold}%)</span>
      )}
    </span>
  );
}

// ─── Criterion comparison table ───────────────────────────────────────────────

function CriterionComparison({ criteria, primaryScores, secondScores }) {
  const pMap = {};
  primaryScores.forEach((s) => { pMap[s.rubric_criterion_id] = s; });
  const sMap = {};
  secondScores.forEach((s) => { sMap[s.rubric_criterion_id] = s; });

  return (
    <div className="rounded-xl border border-slate-700/60 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-800/50 border-b border-slate-700/40 grid grid-cols-[1fr_80px_80px_60px] gap-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
        <span>Criterion</span>
        <span className="text-center">Primary</span>
        <span className="text-center">Second</span>
        <span className="text-center">Diff</span>
      </div>
      {criteria.map((crit) => {
        const ps = pMap[crit.id];
        const ss = sMap[crit.id];
        const pv = ps?.score_value != null ? Number(ps.score_value) : null;
        const sv = ss?.score_value != null ? Number(ss.score_value) : null;
        const diff = pv != null && sv != null ? Math.abs(pv - sv) : null;
        return (
          <div
            key={crit.id}
            data-testid={`criterion-compare-${crit.id}`}
            className="grid grid-cols-[1fr_80px_80px_60px] gap-2 px-4 py-3 border-b border-slate-800/60 items-center"
          >
            <div>
              <span className="text-[10px] font-bold text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded mr-1.5">
                {crit.code}
              </span>
              <span className="text-xs text-slate-300">{crit.title}</span>
              <span className="text-[10px] text-slate-600 ml-1">(/{crit.max_score})</span>
            </div>
            <div className="text-center text-sm font-mono font-semibold text-sky-300">
              {pv ?? "—"}
            </div>
            <div className="text-center text-sm font-mono font-semibold text-teal-300">
              {sv ?? "—"}
            </div>
            <div className={`text-center text-sm font-mono font-bold ${diff != null && diff > 0 ? "text-amber-400" : "text-slate-500"}`}>
              {diff != null ? diff.toFixed(1) : "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Resolution form ──────────────────────────────────────────────────────────

function ResolutionForm({ onSubmit, maxMark, submitting }) {
  const [resolution, setResolution] = useState("");
  const [moderatorMark, setModeratorMark] = useState("");
  const [rationale, setRationale] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canSubmit = resolution !== "" && rationale.trim().length >= 10 &&
    (resolution !== "USE_MODERATOR_VALUE" || (moderatorMark !== "" && Number(moderatorMark) >= 0 && Number(moderatorMark) <= maxMark));

  const handleConfirm = () => {
    if (!canSubmit) return;
    onSubmit({
      resolution,
      moderator_mark: resolution === "USE_MODERATOR_VALUE" ? parseFloat(moderatorMark) : null,
      moderator_rationale: rationale,
    });
    setConfirmOpen(false);
  };

  return (
    <div className="space-y-4" data-testid="resolution-form">
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Resolution Decision</div>

      {/* Resolution options */}
      <div className="space-y-2">
        {RESOLUTION_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            data-testid={`resolution-option-${opt.value}`}
            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              resolution === opt.value
                ? opt.color === "sky"   ? "border-sky-700/60 bg-sky-950/20"
                : opt.color === "teal"  ? "border-teal-700/60 bg-teal-950/20"
                :                        "border-amber-700/60 bg-amber-950/20"
                : "border-slate-700/40 bg-slate-800/20 hover:bg-slate-800/40"
            }`}
          >
            <input
              type="radio"
              name="resolution"
              value={opt.value}
              checked={resolution === opt.value}
              onChange={() => setResolution(opt.value)}
              className="mt-0.5 accent-sky-500 flex-shrink-0"
            />
            <div>
              <div className={`text-sm font-semibold ${
                resolution === opt.value
                  ? opt.color === "sky" ? "text-sky-300" : opt.color === "teal" ? "text-teal-300" : "text-amber-300"
                  : "text-slate-300"
              }`}>{opt.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{opt.desc}</div>
            </div>
          </label>
        ))}
      </div>

      {/* Moderator mark input (only when USE_MODERATOR_VALUE) */}
      {resolution === "USE_MODERATOR_VALUE" && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-950/10 border border-amber-800/30">
          <label className="text-xs text-amber-400 font-medium whitespace-nowrap">Moderator Mark</label>
          <input
            data-testid="moderator-mark-input"
            type="number"
            min={0}
            max={maxMark}
            step={0.5}
            value={moderatorMark}
            onChange={(e) => setModeratorMark(e.target.value)}
            placeholder="0 – 30"
            className="w-24 bg-slate-800 border border-amber-700/60 rounded-lg px-3 py-1.5 text-sm font-mono text-amber-200 focus:outline-none focus:border-amber-500"
          />
          <span className="text-xs text-slate-500">/ {maxMark}</span>
        </div>
      )}

      {/* Rationale */}
      <div>
        <label className="text-xs text-slate-500 font-medium block mb-1.5">Moderator Rationale <span className="text-red-500">*</span></label>
        <textarea
          data-testid="moderator-rationale-input"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder="Provide your moderation rationale (minimum 10 characters)…"
          rows={4}
          className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-300 placeholder:text-slate-600 focus:outline-none focus:border-sky-600 resize-none"
        />
        <div className="text-right text-[10px] text-slate-600 mt-0.5">{rationale.length} chars</div>
      </div>

      {/* Submit */}
      {!confirmOpen ? (
        <button
          data-testid="open-resolution-confirm-btn"
          onClick={() => setConfirmOpen(true)}
          disabled={!canSubmit}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-orange-700 hover:bg-orange-600 disabled:opacity-40 text-white text-sm font-semibold transition-colors"
        >
          <Scale className="w-4 h-4" />
          Review & Submit Resolution
        </button>
      ) : (
        <div className="rounded-xl border border-orange-800/60 bg-orange-950/20 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-orange-300">Confirm Moderation Resolution</div>
              <div className="text-xs text-orange-400/80 mt-0.5">
                This action is <strong>permanent</strong>. Resolution: <strong>{resolution.replace(/_/g, " ")}</strong>
                {resolution === "USE_MODERATOR_VALUE" && moderatorMark && ` → ${moderatorMark} / ${maxMark}`}
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setConfirmOpen(false)}
              className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="confirm-resolution-btn"
              onClick={handleConfirm}
              disabled={submitting}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold disabled:opacity-50 transition-colors"
            >
              {submitting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              Confirm Resolution
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Resolved banner ──────────────────────────────────────────────────────────

function ResolvedBanner({ moderationCase, maxMark }) {
  const srcLabel = {
    USE_PRIMARY: "Primary Mark Accepted",
    USE_SECOND: "Second Mark Accepted",
    USE_MODERATOR_VALUE: "Moderator Override",
  }[moderationCase.resolution] || moderationCase.resolution;

  return (
    <div
      data-testid="moderation-resolved-banner"
      className="flex items-start gap-3 px-5 py-4 rounded-xl border border-violet-800/60 bg-violet-950/20 mb-5"
    >
      <div className="w-9 h-9 rounded-full bg-violet-900/40 border border-violet-800/40 flex items-center justify-center flex-shrink-0">
        <Lock className="w-4 h-4 text-violet-400" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-violet-300">Moderation Resolved — Locked</div>
        <div className="text-xs text-violet-500/80 mt-0.5">
          {srcLabel}.{" "}
          {moderationCase.moderator_mark != null && (
            <span>Moderator mark: <strong className="text-violet-300">{moderationCase.moderator_mark} / {maxMark}</strong>. </span>
          )}
        </div>
        {moderationCase.moderator_rationale && (
          <div className="text-xs text-slate-500 mt-1.5 italic">"{moderationCase.moderator_rationale}"</div>
        )}
      </div>
      {moderationCase.resolved_at && (
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {new Date(moderationCase.resolved_at).toLocaleString()}
        </span>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ModerationWorkspace() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);

  const loadContext = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/evaluation/cases/${caseId}/moderation-context`);
      setCtx(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadContext(); }, [loadContext]);

  const handleResolve = async ({ resolution, moderator_mark, moderator_rationale }) => {
    if (!ctx?.moderation_case?.id) {
      toast.error("No active moderation case found for this evaluation case.");
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch(`/evaluation/moderation-cases/${ctx.moderation_case.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ resolution, moderator_mark, moderator_rationale }),
      });
      toast.success("Moderation resolved successfully.");
      await loadContext();
    } catch (e) {
      if (e.status === 409) {
        toast.error("Moderation case already resolved or invalid state.");
      } else {
        toast.error("Resolution failed: " + e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <EvaluationShell breadcrumb="Moderation">
        <div className="flex items-center justify-center py-20 gap-3 text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading moderation workspace…</span>
        </div>
      </EvaluationShell>
    );
  }

  if (error) {
    return (
      <EvaluationShell breadcrumb="Moderation">
        <div className="flex items-center gap-3 p-6 rounded-xl border border-red-800/50 bg-red-950/20 text-red-300 text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div>
            <strong>Cannot open moderation workspace:</strong> {error}
            <div className="text-xs text-red-400/70 mt-1">
              Ensure you are assigned as moderator for this case and the case is in MODERATION_REQUIRED state.
            </div>
          </div>
        </div>
      </EvaluationShell>
    );
  }

  const {
    case: evalCase,
    rubric,
    criteria = [],
    attempt_meta,
    evidence = [],
    primary_round = {},
    primary_scores = [],
    second_round = {},
    second_scores = [],
    moderation_case = {},
    actor_marker_role,
    feedback = [],
  } = ctx;

  const isResolved = moderation_case?.status === "RESOLVED";
  const diffPct = moderation_case?.mark_difference_pct != null ? Number(moderation_case.mark_difference_pct) : null;
  const maxMark = evalCase?.max_mark ?? 30;

  return (
    <EvaluationShell
      breadcrumb="Moderation"
      title={
        <span className="flex items-center gap-3">
          <button
            data-testid="back-to-moderation-queue-btn"
            onClick={() => navigate("/assessment-evaluation/moderation")}
            className="text-slate-500 hover:text-slate-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          Moderation Workspace
        </span>
      }
    >
      {/* Context bar */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <span className="text-xs text-slate-500 font-mono">Case: {caseId?.slice(0, 20)}…</span>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
          evalCase?.status === "MODERATION_REQUIRED" ? "bg-orange-950 text-orange-300 border-orange-800/60" :
          evalCase?.status === "MODERATED"           ? "bg-violet-950 text-violet-300 border-violet-800/60" :
          "bg-slate-800 text-slate-400 border-slate-700"
        }`}>
          {evalCase?.status?.replace(/_/g, " ")}
        </span>
        {diffPct != null && (
          <DiffBadge diffPct={diffPct} threshold={15} />
        )}
        <span className="text-xs text-orange-400/70 font-medium ml-auto">Moderator</span>
      </div>

      {/* Resolved banner */}
      {isResolved && moderation_case && (
        <ResolvedBanner moderationCase={moderation_case} maxMark={maxMark} />
      )}

      {/* No moderation case yet */}
      {!moderation_case?.id && !isResolved && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-amber-800/40 bg-amber-950/10 text-amber-300 text-sm mb-5">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <div>No active moderation case found. This case may not yet be in MODERATION_REQUIRED state.</div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">

        {/* ── LEFT: Rounds + Evidence ── */}
        <div className="space-y-5">

          {/* Marks comparison */}
          <div className="rounded-xl border border-slate-800 overflow-hidden" data-testid="marks-comparison-panel">
            <div className="px-5 py-3.5 bg-slate-900/60 border-b border-slate-800 flex items-center gap-2">
              <Scale className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-300">Mark Comparison</span>
              {diffPct != null && <DiffBadge diffPct={diffPct} threshold={15} />}
            </div>
            <div className="p-5">
              {/* Round totals */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div
                  data-testid="primary-round-summary"
                  className="rounded-xl border border-sky-800/40 bg-sky-950/10 p-4 text-center"
                >
                  <div className="text-[10px] font-semibold text-sky-500 uppercase tracking-wider mb-1">Primary Mark</div>
                  <div className="text-2xl font-bold font-mono text-sky-300">
                    {primary_round.awarded_mark ?? "—"}
                  </div>
                  <div className="text-xs text-slate-500">/ {primary_round.max_mark_snapshot ?? maxMark}</div>
                  {primary_round.submitted_at && (
                    <div className="text-[10px] text-slate-600 mt-1">
                      {new Date(primary_round.submitted_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <div
                  data-testid="second-round-summary"
                  className="rounded-xl border border-teal-800/40 bg-teal-950/10 p-4 text-center"
                >
                  <div className="text-[10px] font-semibold text-teal-500 uppercase tracking-wider mb-1">Second Mark</div>
                  <div className="text-2xl font-bold font-mono text-teal-300">
                    {second_round.awarded_mark ?? "—"}
                  </div>
                  <div className="text-xs text-slate-500">/ {second_round.max_mark_snapshot ?? maxMark}</div>
                  {second_round.submitted_at && (
                    <div className="text-[10px] text-slate-600 mt-1">
                      {new Date(second_round.submitted_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              {/* Criterion breakdown */}
              {criteria.length > 0 && (primary_scores.length > 0 || second_scores.length > 0) && (
                <CriterionComparison
                  criteria={criteria}
                  primaryScores={primary_scores}
                  secondScores={second_scores}
                />
              )}
            </div>
          </div>

          {/* Evidence (collapsible) */}
          <div className="rounded-xl border border-slate-800 overflow-hidden" data-testid="evidence-panel">
            <button
              type="button"
              onClick={() => setShowEvidence((v) => !v)}
              className="w-full flex items-center gap-2 px-5 py-3.5 bg-slate-900/60 border-b border-slate-800 text-sm font-semibold text-slate-300 hover:text-slate-100 transition-colors"
            >
              <FileText className="w-4 h-4 text-slate-500" />
              Attempt Evidence
              <span className="text-xs text-slate-600 font-normal ml-1">
                ({evidence.length} response{evidence.length !== 1 ? "s" : ""})
              </span>
              <span className="ml-auto">
                {showEvidence ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </span>
            </button>
            {showEvidence && (
              <div className="p-5 space-y-4">
                {attempt_meta?.id ? (
                  <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                    <div>
                      <div className="text-slate-600 mb-0.5">Student ID</div>
                      <div className="text-slate-300 font-mono">{attempt_meta.student_id?.slice(0, 16)}…</div>
                    </div>
                    <div>
                      <div className="text-slate-600 mb-0.5">Submitted</div>
                      <div className="text-slate-300">
                        {attempt_meta.submitted_at ? new Date(attempt_meta.submitted_at).toLocaleString() : "—"}
                      </div>
                    </div>
                  </div>
                ) : null}
                {evidence.length > 0 ? (
                  evidence.map((resp, idx) => (
                    <div
                      key={resp.id}
                      data-testid={`response-item-${idx}`}
                      className="rounded-lg border border-slate-700/60 overflow-hidden"
                    >
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/40 text-xs">
                        <span className="text-slate-600">Q{idx + 1}</span>
                        <span className="text-slate-500">{resp.interaction_type}</span>
                        {resp.is_final && (
                          <span className="ml-auto text-emerald-500 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Final
                          </span>
                        )}
                      </div>
                      <div className="px-3 py-3 bg-slate-900/40 text-xs text-slate-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                        {resp.response_data?.text || resp.response_data?.answer ||
                          JSON.stringify(resp.response_data, null, 2)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-slate-600 italic text-center py-4">
                    No submitted responses on record for this attempt.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Resolution panel ── */}
        <div className="xl:sticky xl:top-0 xl:self-start" data-testid="resolution-panel">
          <div className="rounded-xl border border-slate-800 overflow-hidden">
            <div className="px-5 py-3.5 bg-slate-900/60 border-b border-slate-800 flex items-center gap-2">
              <Scale className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-300">
                {rubric?.title || "Moderation Decision"}
              </span>
              {isResolved && (
                <span className="ml-auto flex items-center gap-1 text-xs text-violet-400">
                  <Lock className="w-3 h-3" /> Resolved
                </span>
              )}
            </div>
            <div className="p-5">
              {isResolved ? (
                <div className="text-xs text-slate-500 text-center py-6">
                  <Lock className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  This moderation case has been resolved. No further changes are possible.
                </div>
              ) : moderation_case?.id ? (
                <ResolutionForm
                  onSubmit={handleResolve}
                  maxMark={maxMark}
                  submitting={submitting}
                />
              ) : (
                <div className="text-xs text-slate-600 italic text-center py-8">
                  No active moderation case. Cannot resolve.
                </div>
              )}
            </div>
          </div>

          {/* Integrity note */}
          <div className="mt-3 flex items-start gap-2 text-[10px] text-slate-600 px-1">
            <ShieldCheck className="w-3 h-3 flex-shrink-0 mt-0.5" />
            <span>
              Resolution is written server-side via the service layer. Canonical resolved_mark
              updates only through the resolution path. SOD: moderator ≠ primary ≠ second.
            </span>
          </div>
        </div>
      </div>
    </EvaluationShell>
  );
}
